import { CatalogApi } from '@backstage/plugin-catalog-react';
import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';

export type CompositeRadarApiOptions = {
  catalog: CatalogApi;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
};

// Loads Tech Radar data from a Catalog Radar entity when id is provided,
// otherwise falls back to the backend tech-radar endpoint.
export class CompositeRadarApi {
  private readonly catalog: CatalogApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(opts: CompositeRadarApiOptions) {
    this.catalog = opts.catalog;
    this.discoveryApi = opts.discoveryApi;
    this.fetchApi = opts.fetchApi;
    this.identityApi = opts.identityApi;
  }

  private normalize(resp: TechRadarLoaderResponse): TechRadarLoaderResponse {
    const quadrants = Array.isArray((resp as any).quadrants) ? [...(resp as any).quadrants] : [];
    const rings = Array.isArray((resp as any).rings) ? [...(resp as any).rings] : [];
    const entries = Array.isArray((resp as any).entries) ? [...(resp as any).entries] : [];

    // Ensure at least 4 quadrants so the Radar layout logic (indices 2 & 3) is safe
    if (quadrants.length < 4) {
      const existingIds = new Set<string>(quadrants.map((q: any) => String(q.id)));
      const placeholders = ['q1', 'q2', 'q3', 'q4']
        .filter(id => !existingIds.has(id))
        .map(id => ({ id, name: id.toUpperCase() }));
      while (quadrants.length < 4 && placeholders.length) {
        quadrants.push(placeholders.shift()!);
      }
      // If still fewer than 4 (e.g., existingIds covers q1..q4 with <4 actual items), pad generic
      while (quadrants.length < 4) {
        const id = `qx${quadrants.length + 1}`;
        quadrants.push({ id, name: id.toUpperCase() });
      }
    }

    // Provide default rings if missing (prevents unknown ring errors later)
    const finalRings = rings.length > 0 ? rings : [
      { id: 'adopt', name: 'Adopt', color: 'rgb(91, 163, 0)' },
      { id: 'trial', name: 'Trial', color: 'rgb(0, 158, 176)' },
      { id: 'asses', name: 'Asses', color: 'rgb(199, 186, 0)' },
      { id: 'hold', name: 'Hold', color: 'rgb(224, 155, 150)' },
    ];

    return { ...resp, quadrants, rings: finalRings, entries } as TechRadarLoaderResponse;
  }

  private convertDates(resp: TechRadarLoaderResponse): TechRadarLoaderResponse {
    return {
      ...resp,
      entries: resp.entries.map(e => ({
        ...e,
        timeline: e.timeline.map(t => ({
          ...t,
          // Convert to Date if string
          date: (typeof t.date === 'string' ? new Date(t.date) : (t.date as Date)),
        })),
      })),
    };
  }

  async load(id?: string): Promise<TechRadarLoaderResponse> {
    // If an entity ref is provided, load from Catalog entity spec.data
    if (id) {
      const entity = await this.catalog.getEntityByRef(id);
      if (!entity) {
        throw new Error(`Radar entity not found: ${id}`);
      }
      const data = entity?.spec?.data as unknown as TechRadarLoaderResponse | undefined;
      if (!data) {
        throw new Error(`Radar entity has no spec.data: ${id}`);
      }
      return this.convertDates(this.normalize(data));
    }

    // Fallback: call backend plugin
    const { token } = await this.identityApi.getCredentials();
    const apiUrl = await this.discoveryApi.getBaseUrl('tech-radar');
    const res = await this.fetchApi.fetch(`${apiUrl}/data`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!res.ok) {
      throw new Error(`Failed to load Tech Radar data, status ${res.status}`);
    }

    const json = (await res.json()) as TechRadarLoaderResponse;
    return this.convertDates(this.normalize(json));
  }
}
